import type { ReactNode } from 'react';

import Text from 'components/core/typography/Text';
import { Clickable } from 'components/ui/shared/Buttons/Clickable';
import { BannerStyle } from 'enums/bannerStyle';

import { BannerContainer, BannerLink, FlexRow, TextContainer } from './Banner.styles';

interface Props {
  /**
   * Accessible label for the icon-only CTA button (e.g. "Close") — without it the CTA has no accessible name
   */
  ctaAriaLabel?: string;
  /**
   * CTA label on right side of banner
   */
  ctaLabel?: ReactNode;
  /**
   * Optional data-testid for CTA button that is rendered
   */
  ctaTestId?: string;
  /**
   * Optional icon to be rendered to the left of the banners title
   */
  icon?: ReactNode;
  /**
   * Callback function for when CTA is clicked
   */
  onClick?: () => void;
  /**
   * Banner style
   */
  styleVariant?: BannerStyle;
  /**
   * Optional data-testid for the banner
   */
  testId?: string;
  /**
   * Banners title — a string, or a node when the copy embeds inline elements (e.g. a link)
   */
  title: ReactNode;
}

const Banner = ({
  ctaAriaLabel,
  ctaLabel,
  ctaTestId,
  icon,
  onClick,
  styleVariant = BannerStyle.NEUTRAL,
  testId,
  title,
}: Props) => (
  <BannerContainer $styleVariant={styleVariant} data-testid={testId}>
    <FlexRow>
      {icon}
      <TextContainer>
        <Text>{title}</Text>
        {!!onClick && !!ctaLabel && (
          <Clickable aria-label={ctaAriaLabel} data-testid={ctaTestId} onClick={onClick}>
            <BannerLink>{ctaLabel}</BannerLink>
          </Clickable>
        )}
      </TextContainer>
    </FlexRow>
  </BannerContainer>
);

export default Banner;
